import { useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"

import toast from "react-hot-toast"

import { Pagination } from "components"
import OutletFilterSelect from "components/forms/outlet-filter-select"
import useSalesAmountYearlyReportRepositories from "repositories/sales-amount-yearly-report"
import { getDefaultOutletFilterValue, resolveOutletFilter } from "helpers/general-helper"

const MONTH_COLUMNS = [
  { key: "jan", label: "JAN" },
  { key: "feb", label: "FEB" },
  { key: "mar", label: "MAR" },
  { key: "apr", label: "APR" },
  { key: "may", label: "MAY" },
  { key: "jun", label: "JUN" },
  { key: "jul", label: "JUL" },
  { key: "aug", label: "AUG" },
  { key: "sep", label: "SEP" },
  { key: "oct", label: "OCT" },
  { key: "nov", label: "NOV" },
  { key: "dec", label: "DEC" },
]

const formatNumber = (value) => Number(value ?? 0).toLocaleString("id-ID", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

export default function List() {
  const [searchParams, setSearchParams] = useSearchParams()

  const year = searchParams.get("year") || String(new Date().getFullYear())
  const outletId = searchParams.get("outlet_id") || getDefaultOutletFilterValue()
  const { outlet_id_csv: resolvedOutletCsv } = resolveOutletFilter(outletId)
  const source = (searchParams.get("source") || "ALL").toUpperCase()
  const page = searchParams.get("page") || "1"
  const perPage = searchParams.get("per_page") || "50"
  const searchParam = searchParams.get("search") || ""
  // ATALLA-348: filter tanggal tunggal (as-of), dikirim sebagai date_to.
  const dateTo = searchParams.get("date_to") || ""

  const SOURCE_OPTIONS = [
    { id: "ALL", name: "Semua Sumber", description: "Semua sumber penjualan" },
    { id: "ATALLA", name: "Atalla", description: "Order dari DB lokal" },
  ]

  const [search, setSearch] = useState(searchParam)

  useEffect(() => setSearch(searchParam), [searchParam])

  const reportParams = useMemo(
    () => ({
      year,
      outlet_id: resolvedOutletCsv || undefined,
      source,
      page,
      per_page: perPage,
      search: searchParam || undefined,
      date_to: dateTo || undefined,
      enabled: true,
    }),
    [year, resolvedOutletCsv, source, page, perPage, searchParam, dateTo]
  )

  const {
    data: reportData,
    isLoading,
  } = useSalesAmountYearlyReportRepositories(reportParams)

  const rows = useMemo(() => reportData?.data ?? [], [reportData])

  const mergeSearchParams = (nextValues) => {
    const nextParams = new URLSearchParams(searchParams)

    Object.entries(nextValues).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") {
        nextParams.delete(key)
      } else {
        nextParams.set(key, value)
      }
    })

    setSearchParams(nextParams)
  }

  const handleExport = () => {
    if (!year) {
      toast.error("Tahun wajib dipilih sebelum export")
      return
    }

    const query = new URLSearchParams({
      year,
      source,
    })
    if (resolvedOutletCsv) query.set("outlet_id", resolvedOutletCsv)
    if (searchParam) query.set("search", searchParam)
    if (dateTo) query.set("date_to", dateTo)

    window.open(
      `${process.env.REACT_APP_INVENTORY_API_URL}/reports/sales-amount-yearly/export?${query.toString()}`,
      "_blank"
    )
  }

  const onSearchChange = (val) => setSearch(val)

  const onSearchSubmit = () => mergeSearchParams({ search: search, page: "1" })

  const summary = useMemo(
    () => ({
      totalProducts: Number(reportData?.summary?.total_products ?? reportData?.total ?? 0),
      totalStock: Number(reportData?.summary?.total_stock ?? 0),
      totalAverage: Number(reportData?.summary?.total_average ?? 0),
    }),
    [reportData]
  )

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 p-5 bg-white border border-neutral-200 rounded-2xl lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-lg font-semibold text-neutral-900">Laporan Nominal Penjualan Pertahun</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Ringkasan nominal penjualan per produk per bulan, lengkap dengan stok aktual saat ini.
          </p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="min-w-[240px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">
              Cari Produk
            </label>
            <input
              type="text"
              value={search}
              onChange={(event) => onSearchChange(event.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onSearchSubmit() } }}
              placeholder="Nama / kode produk"
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
            />
          </div>

          <div className="min-w-[220px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">
              Outlet
            </label>
            <OutletFilterSelect
              value={outletId}
              onChange={(v) => mergeSearchParams({ outlet_id: v, page: "1" })}
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
              title="Outlet"
            />
          </div>

          {/* <div className="min-w-[160px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">
              Tahun
            </label>
            <input
              type="number"
              min="2000"
              max="2100"
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
              value={year}
              onChange={(event) => mergeSearchParams({ year: event.target.value, page: "1" })}
            />
          </div> */}

          {/* ATALLA-348: input tanggal tunggal (as-of) dikirim sebagai date_to. */}
          <div className="min-w-[150px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">
              Tanggal
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(event) => mergeSearchParams({ date_to: event.target.value, page: "1" })}
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
            />
          </div>

          <div className="min-w-[160px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">
              Sumber
            </label>
            <select
              value={source}
              onChange={(event) => mergeSearchParams({ source: event.target.value, page: "1" })}
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
            >
              {SOURCE_OPTIONS.map((opt) => (
                <option key={opt.id} value={opt.id}>{opt.name}</option>
              ))}
            </select>
          </div>

          <div className="min-w-[120px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">
              Per Halaman
            </label>
            <select
              value={perPage}
              onChange={(event) => mergeSearchParams({ per_page: event.target.value, page: "1" })}
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
            >
              {[20, 50, 100].map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
          </div>

          <button
            type="button"
            onClick={handleExport}
            className="px-4 py-2 text-sm font-semibold text-white rounded-lg bg-brand-900 hover:bg-brand-800"
          >
            Ekspor Excel
          </button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Total Produk</div>
          <div className="mt-2 text-2xl font-semibold text-neutral-900">{formatNumber(summary.totalProducts)}</div>
        </div>
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Total Stok Aktual</div>
          <div className="mt-2 text-2xl font-semibold text-neutral-900">{formatNumber(summary.totalStock)}</div>
        </div>
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Total Rata-Rata</div>
          <div className="mt-2 text-2xl font-semibold text-neutral-900">{formatNumber(summary.totalAverage)}</div>
        </div>
      </div>

      <div className="overflow-hidden bg-white border border-neutral-200 rounded-2xl">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-neutral-100">
              <tr>
                <th className="px-4 py-3 font-semibold text-left text-neutral-700 w-[200px] whitespace-normal break-words leading-5">GRUP</th>
                <th className="px-4 py-3 font-semibold text-left text-neutral-700 w-[200px] whitespace-normal break-words leading-5">KODE BARANG</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">STOK</th>
                {MONTH_COLUMNS.map((month) => (
                  <th key={month.key} className="px-4 py-3 font-semibold text-right text-neutral-700">
                    {month.label}
                  </th>
                ))}
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">RATA</th>
                <th className="px-4 py-3 font-semibold text-left text-neutral-700">KET</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={17} className="px-4 py-10 text-center text-neutral-500">
                    Memuat data laporan...
                  </td>
                </tr>
              )}

              {!isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={17} className="px-4 py-10 text-center text-neutral-500">
                    Tidak ada data penjualan untuk filter yang dipilih.
                  </td>
                </tr>
              )}

              {!isLoading &&
                rows.map((row, index) => (
                  <tr key={`${row.kode_barang}-${index}`} className="border-t border-neutral-200 hover:bg-brand-50 transition-colors">
                    <td className="px-4 py-3 text-neutral-800 w-[200px] whitespace-normal break-words leading-5">{row.group || "-"}</td>
                    <td className="px-4 py-3 text-neutral-800 w-[200px] whitespace-normal break-words leading-5">{row.kode_barang || "-"}</td>
                    <td className="px-4 py-3 text-right text-neutral-800">{formatNumber(row.stock)}</td>
                    {MONTH_COLUMNS.map((month) => (
                      <td key={month.key} className="px-4 py-3 text-right text-neutral-800">
                        {formatNumber(row[month.key])}
                      </td>
                    ))}
                    <td className="px-4 py-3 font-semibold text-right text-neutral-900">
                      {formatNumber(row.rata)}
                    </td>
                    <td className="px-4 py-3 text-neutral-500">{row.ket || ""}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-4 border-t border-neutral-200">
          <Pagination
            links={reportData?.links}
            from={reportData?.from}
            to={reportData?.to}
            total={reportData?.total}
          />
        </div>
      </div>
    </div>
  )
}
